import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import {
    addLiquidity,
    derivePoolAddress,
    getBots,
    getPool,
    listPools,
    removeLiquidity,
    startBot,
    stopBot
} from '../../services/OrcaService';
import './Orca.css';

const DEFAULT_FORM = {
    secretKey: '',
    rpcUrl: '',
    commitment: 'confirmed',
    poolAddress: '',
    tokenMintA: '',
    tokenMintB: '',
    tickSpacing: '64',
    inputMint: '',
    inputAmount: '',
    slippageBps: '100',
    positionAddress: '',
    tickLowerIndex: '',
    tickUpperIndex: '',
    liquidityAmount: '',
    triggerPrice: '',
    triggerDirection: 'above',
    intervalMs: '30000',
    maxSwaps: '1',
    executeSwap: false,
    useServerSigner: false
};

function Orca() {
    const navigate = useNavigate();
    const { connected, publicKey, wallet } = useWallet();
    const [form, setForm] = useState(DEFAULT_FORM);
    const [result, setResult] = useState(null);
    const [bots, setBots] = useState([]);
    const [pools, setPools] = useState([]);
    const [poolSearch, setPoolSearch] = useState('');
    const [poolLimit, setPoolLimit] = useState('25');
    const [rangePreset, setRangePreset] = useState('medium');
    const [loading, setLoading] = useState('');
    const [notification, setNotification] = useState({ type: '', text: '' });

    useEffect(() => {
        refreshBots();
        loadPools(false);
    }, []);

    function updateField(event) {
        const { name, value, type, checked } = event.target;
        setForm(current => ({
            ...current,
            [name]: type === 'checkbox' ? checked : value
        }));
    }

    function basePayload() {
        return {
            secretKey: form.useServerSigner ? form.secretKey || undefined : undefined,
            walletPublicKey: publicKey ? publicKey.toBase58() : undefined,
            rpcUrl: form.rpcUrl || undefined,
            commitment: form.commitment || undefined
        };
    }

    function showSuccess(text, data) {
        setResult(data);
        setNotification({ type: 'success', text });
    }

    function showError(err) {
        const text = err.response?.data || err.message || 'Erro ao executar operação de pool.';
        setNotification({ type: 'error', text });
        setResult({ error: text });
    }

    async function run(action, text, callback) {
        setLoading(action);
        try {
            const data = await callback();
            showSuccess(text, data);
            return data;
        } catch (err) {
            console.error(err);
            showError(err);
        } finally {
            setLoading('');
        }
    }

    async function refreshBots() {
        try {
            setBots(await getBots());
        } catch (err) {
            console.error(err);
        }
    }

    async function loadPools(refresh = false) {
        setLoading('pools');
        try {
            const data = await listPools({
                ...basePayload(),
                search: poolSearch,
                limit: poolLimit,
                refresh
            });
            setPools(data);
        } catch (err) {
            console.error(err);
            showError(err);
        } finally {
            setLoading('');
        }
    }

    function selectPool(pool) {
        setForm(current => ({
            ...current,
            poolAddress: pool.address,
            inputMint: current.inputMint || (pool.display?.tokenA?.mint || pool.tokenA.mint)
        }));
        setResult(pool);
        navigate(`/orca/liquidity/${pool.address}`);
    }

    function onRangePresetChange(preset) {
        setRangePreset(preset);
        if (!selectedPool) return;
        const range = getRangeForPool(selectedPool, preset);
        setForm(current => ({
            ...current,
            tickLowerIndex: range.lower,
            tickUpperIndex: range.upper
        }));
    }

    function requireField(field, label) {
        if (!form[field]) {
            setNotification({ type: 'error', text: `${label} é obrigatório.` });
            return false;
        }
        return true;
    }

    function requireConnectedWallet() {
        if (!connected || !publicKey) {
            setNotification({ type: 'error', text: 'Conecte uma carteira Solana antes de executar esta operação.' });
            return false;
        }
        return true;
    }

    function requireSignerMode() {
        if (form.useServerSigner) return requireField('secretKey', 'Secret Key');
        setNotification({
            type: 'warning',
            text: 'Carteira conectada. Para enviar transações assinadas pela wallet, falta ativar o backend de transação serializada. Use cotação/consulta agora ou modo legado avançado.'
        });
        return false;
    }

    async function onGetPool() {
        if (!requireField('poolAddress', 'Pool Address')) return;
        await run('pool', 'Pool consultado com sucesso.', () => getPool(form.poolAddress, basePayload()));
    }

    async function onDerivePool() {
        if (!requireField('tokenMintA', 'Token Mint A')) return;
        if (!requireField('tokenMintB', 'Token Mint B')) return;
        await run('derive', 'Endereço da pool derivado.', () => derivePoolAddress({
            tokenMintA: form.tokenMintA,
            tokenMintB: form.tokenMintB,
            tickSpacing: form.tickSpacing
        }));
    }

    async function onAddLiquidity() {
        if (!window.confirm('Adicionar liquidez real na pool?')) return;
        if (!requireConnectedWallet()) return;
        if (!requireSignerMode()) return;
        if (!requireField('poolAddress', 'Pool Address')) return;
        if (!requireField('inputMint', 'Input Mint')) return;
        if (!requireField('inputAmount', 'Input Amount')) return;
        if (!requireField('tickLowerIndex', 'Tick Lower')) return;
        if (!requireField('tickUpperIndex', 'Tick Upper')) return;
        await run('addLiquidity', 'Liquidez enviada para a pool.', () => addLiquidity({
            ...basePayload(),
            poolAddress: form.poolAddress,
            positionAddress: form.positionAddress || undefined,
            inputMint: form.inputMint,
            inputAmount: form.inputAmount,
            tickLowerIndex: form.tickLowerIndex,
            tickUpperIndex: form.tickUpperIndex,
            slippageBps: form.slippageBps,
            initTickArrays: true
        }));
    }

    async function onRemoveLiquidity(closePosition = false) {
        if (!window.confirm(closePosition ? 'Fechar posição real na pool?' : 'Remover liquidez real na pool?')) return;
        if (!requireConnectedWallet()) return;
        if (!requireSignerMode()) return;
        if (!requireField('positionAddress', 'Position Address')) return;
        await run('removeLiquidity', 'Operação de liquidez enviada.', () => removeLiquidity({
            ...basePayload(),
            positionAddress: form.positionAddress,
            liquidityAmount: form.liquidityAmount || undefined,
            slippageBps: form.slippageBps,
            collectFees: true,
            collectRewards: true,
            closePosition
        }));
    }

    async function onStartBot() {
        if (form.executeSwap && (!form.useServerSigner || !form.secretKey)) {
            setNotification({ type: 'error', text: 'No momento, Secret Key em modo legado é obrigatória quando o bot executa swap.' });
            return;
        }
        if (!requireField('poolAddress', 'Pool Address')) return;
        await run('bot', 'Bot de pool iniciado.', async () => {
            const data = await startBot({
                ...basePayload(),
                poolAddress: form.poolAddress,
                inputMint: form.inputMint || undefined,
                inputAmount: form.inputAmount || undefined,
                slippageBps: form.slippageBps,
                triggerPrice: form.triggerPrice || undefined,
                triggerDirection: form.triggerDirection,
                intervalMs: form.intervalMs,
                maxSwaps: form.maxSwaps,
                executeSwap: form.executeSwap
            });
            await refreshBots();
            return data;
        });
    }

    async function onStopBot(id) {
        await run('botStop', 'Bot de pool parado.', async () => {
            await stopBot(id);
            await refreshBots();
            return { id, stopped: true };
        });
    }

    function renderInput(name, label, placeholder, type = 'text') {
        return (
            <div className="orca-field">
                <label>{label}</label>
                <input
                    className="form-control"
                    name={name}
                    type={type}
                    value={form[name]}
                    placeholder={placeholder}
                    onChange={updateField}
                />
            </div>
        );
    }

    function shortAddress(address) {
        if (!address) return '';
        return `${address.slice(0, 4)}...${address.slice(-4)}`;
    }

    function poolTokenLabel(token) {
        return token?.symbol || shortAddress(token?.mint);
    }

    function displayToken(pool, side) {
        return pool?.display?.[side] || pool?.[side];
    }

    function displayPrice(pool) {
        return pool?.display?.price || pool?.price;
    }

    function formatNumber(value, maximumFractionDigits = 8) {
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) return value || '-';
        return parsed.toLocaleString('pt-BR', { maximumFractionDigits });
    }

    function formatCurrency(value) {
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) return '$ -';
        return parsed.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0
        });
    }

    function safeNumber(value) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    function getRangeForPool(pool, preset) {
        const currentTick = Number(pool?.tickCurrentIndex || 0);
        const spacing = Number(pool?.tickSpacing || 1);
        const widths = {
            narrow: 20,
            medium: 80,
            wide: 220,
            full: 443636
        };

        if (preset === 'full') {
            return {
                lower: Math.ceil(-443636 / spacing) * spacing,
                upper: Math.floor(443636 / spacing) * spacing
            };
        }

        const width = (widths[preset] || widths.medium) * spacing;
        return {
            lower: Math.floor((currentTick - width) / spacing) * spacing,
            upper: Math.ceil((currentTick + width) / spacing) * spacing
        };
    }

    function tickToRelativePrice(tick, pool) {
        if (!pool || tick === '' || tick === undefined || tick === null) return '-';
        const currentTick = Number(pool.tickCurrentIndex || 0);
        const currentPrice = Number(pool.price || 0);
        if (!Number.isFinite(currentPrice) || currentPrice <= 0) return '-';
        const price = currentPrice * Math.pow(1.0001, Number(tick) - currentTick);
        return formatNumber(price, 6);
    }

    function tokenIcon(token) {
        if (token?.imageUrl) return <img src={token.imageUrl} alt={poolTokenLabel(token)} />;
        return <span>{(poolTokenLabel(token) || '?').slice(0, 1)}</span>;
    }

    const selectedPool = pools.find(pool => pool.address === form.poolAddress)
        || (result && result.address ? result : null);
    const rankedPools = useMemo(() => rankPools(pools), [pools]);
    const totalTvl = pools.reduce((sum, pool) => sum + (Number(pool.tvlUsdc) || 0), 0);
    const totalVolume24h = pools.reduce((sum, pool) => sum + (Number(pool.volume24h) || 0), 0);
    const totalRewards24h = pools.reduce((sum, pool) => sum + safeNumber(pool.rewards24h), 0);

    return (
        <>
            <main className="content orca-page orca-full-page">
                <div className="orca-container">
                    <section className="orca-hero">
                        <div className="orca-hero-copy">
                            <span className="orca-eyebrow">Solana Concentrated Liquidity</span>
                            <h1>Trade e liquidez em Pool</h1>
                            <p>Explore pools, acompanhe TVL/volume e teste swaps na Solana.</p>
                            <div className="orca-hero-actions">
                                <button className="orca-primary-btn" onClick={() => loadPools(true)} disabled={loading === 'pools'}>
                                    {loading === 'pools' ? 'Sincronizando...' : 'Atualizar mercados'}
                                </button>
                                <button className="orca-ghost-btn" onClick={() => window.location.href = form.poolAddress ? `/orca/swap?pool=${form.poolAddress}` : '/orca/swap'} disabled={!form.poolAddress}>
                                    Abrir swap
                                </button>
                                <button className="orca-ghost-btn" onClick={() => navigate('/orca/portfolio')}>
                                    Meu portfólio
                                </button>
                                <button className="orca-ghost-btn" onClick={() => navigate('/orca/positions')}>
                                    Minhas posições
                                </button>
                                <button className="orca-ghost-btn" onClick={onGetPool} disabled={!form.poolAddress || loading === 'pool'}>
                                    Detalhes da pool
                                </button>
                            </div>
                        </div>
                        <div className="orca-wallet-panel">
                            <WalletMultiButton />
                            <div className="orca-wallet-status">
                                <strong>{connected ? 'Carteira conectada' : 'Conectar carteira'}</strong>
                                <span>{connected && publicKey ? shortAddress(publicKey.toBase58()) : 'Phantom, Solflare ou WalletConnect'}</span>
                                {wallet?.adapter?.name ? <small>{wallet.adapter.name}</small> : <small>Selecione sua carteira.</small>}
                            </div>
                        </div>
                    </section>

                    <section className="orca-stats-grid">
                        <div className="orca-stat-card">
                            <small>Pools carregadas</small>
                            <strong>{pools.length}</strong>
                        </div>
                        <div className="orca-stat-card">
                            <small>TVL listado</small>
                            <strong>{formatCurrency(totalTvl)}</strong>
                        </div>
                        <div className="orca-stat-card">
                            <small>Volume 24h listado</small>
                            <strong>{formatCurrency(totalVolume24h)}</strong>
                        </div>
                        <div className="orca-stat-card">
                            <small>Recompensas 24h</small>
                            <strong>{formatCurrency(totalRewards24h)}</strong>
                        </div>
                    </section>

                    <section className="orca-market-layout single">
                        <div className="orca-market-card">
                            <div className="orca-section-header">
                                <div>
                                    <h2>Mercados classificados</h2>
                                    <p>Pools ordenadas do melhor ao pior por TVL, taxas e volume.</p>
                                </div>
                                <span className="orca-source-pill">Top pools</span>
                            </div>
                            <div className="orca-pool-toolbar">
                                <input
                                    className="form-control"
                                    value={poolSearch}
                                    placeholder="Buscar SOL, USDC, mint ou endereço"
                                    onChange={event => setPoolSearch(event.target.value)}
                                    onKeyDown={event => {
                                        if (event.key === 'Enter') loadPools(false);
                                    }}
                                />
                                <select className="form-control" value={poolLimit} onChange={event => setPoolLimit(event.target.value)}>
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                                <button className="orca-ghost-btn" disabled={loading === 'pools'} onClick={() => loadPools(false)}>
                                    Buscar
                                </button>
                            </div>
                            <div className="orca-pool-grid">
                                {rankedPools.length ? rankedPools.map((pool, index) => (
                                    <button
                                        className={`orca-pool-card ${form.poolAddress === pool.address ? 'selected' : ''}`}
                                        key={pool.address}
                                        onClick={() => selectPool(pool)}
                                    >
                                        <div className="orca-pool-card-header">
                                            <span className="orca-rank-badge">#{index + 1}</span>
                                            <div className="orca-token-pair">
                                                <div className="orca-token-icons">
                                                    <div className="orca-token-icon">{tokenIcon(displayToken(pool, 'tokenA'))}</div>
                                                    <div className="orca-token-icon second">{tokenIcon(displayToken(pool, 'tokenB'))}</div>
                                                </div>
                                                <div>
                                                    <strong>{poolTokenLabel(displayToken(pool, 'tokenA'))} / {poolTokenLabel(displayToken(pool, 'tokenB'))}</strong>
                                                    <small>{shortAddress(pool.address)} · fee {pool.feeRate}</small>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="orca-pool-card-metrics">
                                            <div>
                                                <small>Preço</small>
                                                <strong>{formatNumber(displayPrice(pool), 8)}</strong>
                                                <span>{poolTokenLabel(displayToken(pool, 'tokenB'))} por {poolTokenLabel(displayToken(pool, 'tokenA'))}</span>
                                            </div>
                                            <div>
                                                <small>Taxas 24h</small>
                                                <strong>{pool.fees24h ? formatCurrency(pool.fees24h) : '-'}</strong>
                                            </div>
                                            <div>
                                                <small>TVL</small>
                                                <strong>{pool.tvlUsdc ? formatCurrency(pool.tvlUsdc) : formatNumber(pool.liquidity, 0)}</strong>
                                            </div>
                                            <div>
                                                <small>Volume 24h</small>
                                                <strong>{pool.volume24h ? formatCurrency(pool.volume24h) : '-'}</strong>
                                            </div>
                                        </div>
                                    </button>
                                )) : <div className="orca-empty-state">Carregando mercados...</div>}
                            </div>
                        </div>

                    </section>
                </div>
                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </>
    );
}

function rankPools(pools = []) {
    const max = {
        tvlUsdc: maxMetric(pools, 'tvlUsdc'),
        fees24h: maxMetric(pools, 'fees24h'),
        volume24h: maxMetric(pools, 'volume24h'),
        rewards24h: maxMetric(pools, 'rewards24h'),
        fees7d: maxMetric(pools, 'fees7d'),
        rewards7d: maxMetric(pools, 'rewards7d')
    };

    return pools
        .map(pool => ({
            ...pool,
            rankingScore:
                normalizedMetric(pool.tvlUsdc, max.tvlUsdc) * 0.35
                + normalizedMetric(pool.fees24h, max.fees24h) * 0.25
                + normalizedMetric(pool.rewards24h, max.rewards24h) * 0.2
                + normalizedMetric(pool.volume24h, max.volume24h) * 0.12
                + normalizedMetric(pool.fees7d, max.fees7d) * 0.05
                + normalizedMetric(pool.rewards7d, max.rewards7d) * 0.03
        }))
        .sort((left, right) => right.rankingScore - left.rankingScore
            || Number(right.tvlUsdc || 0) - Number(left.tvlUsdc || 0));
}

function maxMetric(items, key) {
    return items.reduce((max, item) => Math.max(max, Number(item?.[key]) || 0), 0);
}

function normalizedMetric(value, max) {
    if (!max) return 0;
    return (Number(value) || 0) / max;
}


export default Orca;
